'use client';
import { useState, useEffect, useCallback } from 'react';
import type { GameRules } from '@/lib/gameEngine';

export type VisualTheme = 'dark' | 'light';

export interface GameSettings {
  audioVolume: number;          // 0–100
  animationSpeed: 'slow' | 'normal' | 'fast' | 'off';
  visualTheme: VisualTheme;
  rules: {
    balancingPhase: boolean;
    texasTieBreak: boolean;
    sweetwaterRule: boolean;
    chunnelRule: boolean;
  };
}

export const DEFAULT_SETTINGS: GameSettings = {
  audioVolume: 70,
  animationSpeed: 'normal',
  visualTheme: 'dark',
  rules: {
    balancingPhase: true,
    texasTieBreak: true,
    sweetwaterRule: false,
    chunnelRule: false,
  },
};

const STORAGE_KEY = 'vinciSettings';

export function useSettings() {
  const [settings, setSettings] = useState<GameSettings>(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<GameSettings>;
        setSettings((prev) => ({ ...prev, ...parsed, rules: { ...prev.rules, ...(parsed.rules ?? {}) } }));
      }
    } catch {
      // ignore parse errors
    }
    setLoaded(true);
  }, []);

  const updateSettings = useCallback((patch: Partial<GameSettings>) => {
    setSettings((prev) => {
      const next = {
        ...prev,
        ...patch,
        rules: { ...prev.rules, ...(patch.rules ?? {}) },
      };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_SETTINGS));
    } catch {
      // ignore
    }
  }, []);

  /** Derive a GameRules object from current settings */
  const getGameRules = useCallback((): GameRules => settings.rules, [settings.rules]);

  /** Animation duration multiplier based on speed setting */
  const animationMultiplier = (() => {
    switch (settings.animationSpeed) {
      case 'slow': return 2;
      case 'fast': return 0.5;
      case 'off': return 0;
      default: return 1;
    }
  })();

  /** Volume as 0–1 float for Web Audio API */
  const volumeGain = settings.audioVolume / 100;

  return {
    settings,
    loaded,
    updateSettings,
    resetSettings,
    getGameRules,
    animationMultiplier,
    volumeGain,
  };
}
